import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PlayCircle, Lock, CheckCircle, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import { QuizCelebrationModal, ModuleCelebrationModal, QuizFailModal } from '@/components/CelebrationModal';
import { Milestone25Modal, Milestone50Modal, Milestone75Modal, Milestone100Modal } from '@/components/MilestoneCelebration';
import { AssignmentPromptModal } from '@/components/course/AssignmentPromptModal';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function CourseView({ user, onLogout }) {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [courseDetails, setCourseDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0); // Add refresh key to force lesson re-evaluation
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [showAssignmentPrompt, setShowAssignmentPrompt] = useState(false);
  const [triggerAssignmentModal, setTriggerAssignmentModal] = useState(null); // {lessonId, assignmentId}
  
  // CRITICAL: Optimistic progress tracking for instant updates
  const [localCompletedLessons, setLocalCompletedLessons] = useState(new Set());
  const [optimisticProgress, setOptimisticProgress] = useState(null);

  useEffect(() => {
    fetchCourseDetails();
  }, [courseId]);

  const calculateOptimisticProgress = (modules, serverCompleted, localCompleted) => {
    // Count all lessons
    const allLessons = modules.flatMap(m => m.lessons || []);
    const totalLessons = allLessons.length;
    
    if (totalLessons === 0) return { completed_lessons: 0, total_lessons: 0, progress_percentage: 0 };
    
    // Combine server completed lessons with local completed lessons
    const completedSet = new Set([
      ...allLessons.filter(l => l.completed).map(l => l.id),
      ...Array.from(localCompleted)
    ]);
    
    const completedCount = completedSet.size;
    const percentage = (completedCount / totalLessons) * 100;
    
    return {
      completed_lessons: completedCount,
      total_lessons: totalLessons,
      progress_percentage: percentage
    };
  };

  const fetchCourseDetails = async () => {
    try {
      // OPTIMIZED: Single API call instead of multiple nested calls
      const res = await axios.get(`${API}/courses/${courseId}/full`, { headers: getAuthHeaders() });
      
      const { course, modules, progress } = res.data;

      // Calculate optimistic progress immediately
      const optimistic = calculateOptimisticProgress(
        modules, 
        progress,
        localCompletedLessons
      );
      
      setOptimisticProgress(optimistic);

      setCourseDetails({
        ...course,
        modules: modules,
        progress: progress
      });
    } catch (error) {
      console.error('Failed to fetch course details:', error);
      toast.error('Failed to load course details');
    } finally {
      setLoading(false);
    }
  };

  // Enhanced refresh function that forces lesson access re-evaluation
  const handleRefresh = async () => {
    await fetchCourseDetails();
    await checkPendingAssignments();
    setRefreshKey(prev => prev + 1); // Increment refresh key to force lesson re-evaluation
  };

  const checkPendingAssignments = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}/pending-assignments`, {
        headers: getAuthHeaders()
      });
      
      if (response.data && response.data.length > 0) {
        setPendingAssignments(response.data);
        setShowAssignmentPrompt(true);
      }
    } catch (error) {
      console.error('Failed to check pending assignments:', error);
    }
  };

  // Check for pending assignments on initial load
  useEffect(() => {
    if (courseDetails && user?.role === 'student') {
      checkPendingAssignments();
    }
  }, [courseDetails, courseId]);

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="courses"><div className="p-8">Loading...</div></Layout>;
  }

  if (!courseDetails) {
    return <Layout user={user} onLogout={onLogout} currentPage="courses"><div className="p-8">Course not found</div></Layout>;
  }

  const getThumbnailUrl = () => {
    if (!courseDetails.thumbnail_url) return null;
    if (courseDetails.thumbnail_url.startsWith('http')) return courseDetails.thumbnail_url;
    return `${BACKEND_URL}${courseDetails.thumbnail_url}`;
  };

  return (
    <Layout user={user} onLogout={onLogout} currentPage="courses">
      <div className="p-6 lg:p-8 space-y-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/courses')}
          className="text-primary-blue hover:text-[#1874CD] font-medium"
        >
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={2} />
          Back to Courses
        </Button>

        {/* Course Thumbnail - Full Width at Top */}
        {courseDetails.thumbnail_url && (
          <div className="relative rounded-2xl overflow-hidden h-80 border-0 card-shadow">
            <img src={getThumbnailUrl()} alt={courseDetails.title} className="w-full h-full object-cover" />
          </div>
        )}

        {/* Course Name and Days Left on Same Line */}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-navy">{courseDetails.title}</h1>
          {courseDetails.duration_value && (
            <span className="text-sm font-medium text-[#4D4D4D] whitespace-nowrap ml-4">
              {(() => {
                const storageKey = `course_assignment_${courseDetails.id}_${user.id}`;
                let assignmentDate = localStorage.getItem(storageKey);
                
                if (!assignmentDate) {
                  assignmentDate = new Date().toISOString();
                  localStorage.setItem(storageKey, assignmentDate);
                }
                
                let totalDays = courseDetails.duration_value;
                if (courseDetails.duration_unit === 'weeks') totalDays *= 7;
                if (courseDetails.duration_unit === 'months') totalDays *= 30;
                
                const assigned = new Date(assignmentDate);
                const today = new Date();
                const msPerDay = 1000 * 60 * 60 * 24;
                const daysElapsed = Math.floor((today - assigned) / msPerDay);
                const daysLeft = Math.max(0, totalDays - daysElapsed);
                
                return `${daysLeft} days left`;
              })()}
            </span>
          )}
        </div>

        {/* Progress Section - Immediately Below Course Name */}
        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardContent className="p-6">
            <div className="space-y-3">
              {/* Progress Bar with Percentage on Right */}
              <div className="flex items-center justify-between">
                <span className="font-semibold text-base text-navy">Course Progress</span>
                <span className="font-bold text-2xl text-primary-blue">
                  {(optimisticProgress?.progress_percentage || courseDetails?.progress.progress_percentage || 0).toFixed(0)}%
                </span>
              </div>
              
              {/* Progress Bar */}
              <Progress value={optimisticProgress?.progress_percentage || courseDetails?.progress.progress_percentage || 0} className="h-3" />
              
              {/* Lessons Completed Indicator */}
              <p className="text-sm text-[#4D4D4D] font-light">
                {optimisticProgress?.completed_lessons || courseDetails?.progress.completed_lessons} of {optimisticProgress?.total_lessons || courseDetails?.progress.total_lessons} lessons completed
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Course Metadata Grid */}
        {(courseDetails.skills_learned || courseDetails.course_includes?.length > 0 || courseDetails.subjects_covered?.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Skills Learned */}
            {courseDetails.skills_learned && (
              <Card className="border-0 card-shadow bg-white rounded-2xl">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-navy mb-3">Skills You'll Learn</h3>
                  <div className="text-sm text-[#4D4D4D] whitespace-pre-line font-light">
                    {courseDetails.skills_learned}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Course Includes */}
            {courseDetails.course_includes?.length > 0 && (
              <Card className="border-0 card-shadow bg-white rounded-2xl">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-navy mb-3">What's Included</h3>
                  <div className="space-y-2">
                    {courseDetails.course_includes.map((item, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-primary-blue flex-shrink-0" strokeWidth={2} />
                        <span className="text-sm text-[#4D4D4D]">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Course Info */}
            <Card className="border-0 card-shadow bg-white rounded-2xl">
              <CardContent className="p-6 space-y-3">
                <h3 className="text-lg font-bold text-navy mb-3">Course Info</h3>
                
                {courseDetails.subjects_covered?.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-[#4D4D4D] mb-2">Subjects</p>
                    <div className="flex flex-wrap gap-2">
                      {courseDetails.subjects_covered.map((subject, index) => (
                        <span key={index} className="px-3 py-1 bg-primary-blue/10 text-primary-blue text-xs font-semibold rounded-full">
                          {subject}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {courseDetails.grade_levels?.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-[#4D4D4D] mb-2">Grade Levels</p>
                    <div className="flex flex-wrap gap-2">
                      {courseDetails.grade_levels.map((grade, index) => (
                        <span key={index} className="px-3 py-1 bg-[#E7E7E7] text-navy text-xs font-semibold rounded-full">
                          {grade}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {courseDetails.duration_value && (
                  <div>
                    <p className="text-sm font-semibold text-[#4D4D4D] mb-1">Duration</p>
                    <p className="text-sm text-navy">{courseDetails.duration_value} {courseDetails.duration_unit}</p>
                  </div>
                )}

                {courseDetails.board_alignment && (
                  <div>
                    <p className="text-sm font-semibold text-[#4D4D4D] mb-1">Board</p>
                    <p className="text-sm text-navy">{courseDetails.board_alignment}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Course Modules Section */}
        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardContent className="p-6">

            <div className="space-y-4">
              {courseDetails?.modules.map((module, moduleIndex) => (
                <div key={module.id} className="border-0 card-shadow rounded-2xl p-6 bg-white card-hover transition-smooth">
                  <h3 className="text-xl font-bold mb-4 text-navy flex items-center">
                    <span className="w-8 h-8 rounded-full bg-primary-blue text-white flex items-center justify-center text-sm mr-3 font-bold">
                      {moduleIndex + 1}
                    </span>
                    {module.title}
                  </h3>
                  <div className="space-y-3">
                    {module.lessons.map((lesson, lessonIndex) => (
                      <LessonItem 
                        key={lesson.id} 
                        lesson={lesson} 
                        moduleIndex={moduleIndex} 
                        lessonIndex={lessonIndex} 
                        onRefresh={handleRefresh}
                        courseDetails={courseDetails}
                        refreshKey={refreshKey}
                        triggerAssignmentModal={triggerAssignmentModal}
                        onAssignmentModalTriggered={() => setTriggerAssignmentModal(null)}
                        setLocalCompletedLessons={setLocalCompletedLessons}
                        localCompletedLessons={localCompletedLessons}
                        calculateOptimisticProgress={calculateOptimisticProgress}
                        setOptimisticProgress={setOptimisticProgress}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Assignment Prompt Popup */}
      {showAssignmentPrompt && pendingAssignments.length > 0 && (
        <AssignmentPromptModal 
          pendingAssignments={pendingAssignments}
          onSubmitAssignment={(assignmentId) => {
            setShowAssignmentPrompt(false);
            // Find the lesson that contains this assignment
            const pendingAssignment = pendingAssignments.find(pa => pa.assignment.id === assignmentId);
            if (pendingAssignment) {
              setTriggerAssignmentModal({
                lessonId: pendingAssignment.lesson.id,
                assignmentId: assignmentId
              });
            }
          }}
          onRemindLater={() => setShowAssignmentPrompt(false)}
          onClose={() => setShowAssignmentPrompt(false)}
        />
      )}
    </Layout>
  );
}

function LessonItem({ lesson, moduleIndex, lessonIndex, onRefresh, courseDetails, refreshKey, triggerAssignmentModal, onAssignmentModalTriggered, setLocalCompletedLessons, localCompletedLessons, calculateOptimisticProgress, setOptimisticProgress }) {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [canAccess, setCanAccess] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [showAnswerFeedback, setShowAnswerFeedback] = useState(false); // FIXED: Show correct/incorrect answers
  const [isCompleted, setIsCompleted] = useState(false);
  const [showQuizCelebration, setShowQuizCelebration] = useState(false);
  const [showQuizFail, setShowQuizFail] = useState(false);
  const [moduleCheckPending, setModuleCheckPending] = useState(false); // FIXED: Track if module check is pending
  const [videoWatchTime, setVideoWatchTime] = useState(0);
  const [videoMarkedComplete, setVideoMarkedComplete] = useState(false);
  const [milestoneData, setMilestoneData] = useState(null);
  const [showMilestone25, setShowMilestone25] = useState(false);
  const [showMilestone50, setShowMilestone50] = useState(false);
  const [showMilestone75, setShowMilestone75] = useState(false);
  const [showMilestone100, setShowMilestone100] = useState(false);
  const [showModuleCelebration, setShowModuleCelebration] = useState(false);
  const [completedModuleName, setCompletedModuleName] = useState('');
  const [justCompleted, setJustCompleted] = useState(() => {
    // Check localStorage for persisted completion state
    const cached = localStorage.getItem(`lesson_completed_${lesson.id}`);
    return cached === 'true';
  }); // CRITICAL: Track if lesson was just completed locally
  
  // Assignment state
  const [assignments, setAssignments] = useState([]);
  const [assignmentSubmissions, setAssignmentSubmissions] = useState({});
  const [submissionContent, setSubmissionContent] = useState({});
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    checkAccess();
    loadAssignments();
  }, [refreshKey]);

  const checkAccess = async () => {
    try {
      const res = await axios.get(`${API}/progress/lesson/${lesson.id}/can-access`, {
        headers: getAuthHeaders()
      });
      setCanAccess(res.data.can_access);
      
      // Check lesson progress for visual completion
      const progressRes = await axios.get(`${API}/progress/lesson/${lesson.id}`, {
        headers: getAuthHeaders()
      });
      
      // CRITICAL FIX: Don't overwrite completion state if lesson was just completed locally
      // This prevents the race condition where backend hasn't finished updating yet
      if (progressRes.data) {
        const serverCompleted = progressRes.data.visually_completed === true;
        
        // If lesson was just completed locally, keep it completed
        // If server says completed, definitely mark as completed
        // Only mark as not completed if server says so AND we didn't just complete it
        if (serverCompleted || justCompleted) {
          setIsCompleted(true);
          // Once server confirms, clear the justCompleted flag and localStorage
          if (serverCompleted && justCompleted) {
            setJustCompleted(false);
            localStorage.removeItem(`lesson_completed_${lesson.id}`);
          }
        } else {
          setIsCompleted(false);
        }
      }
    } catch (error) {
      console.error('Failed to check access:', error);
    }
  };

  const loadAssignments = async () => {
    try {
      // Load assignments for this lesson
      const assignmentsRes = await axios.get(`${API}/assignments/lesson/${lesson.id}`, {
        headers: getAuthHeaders()
      });
      setAssignments(assignmentsRes.data);
      
      // Load existing submissions for each assignment
      const submissions = {};
      for (const assignment of assignmentsRes.data) {
        try {
          const submissionRes = await axios.get(`${API}/assignments/${assignment.id}/submission`, {
            headers: getAuthHeaders()
          });
          if (submissionRes.data) {
            submissions[assignment.id] = submissionRes.data;
            // Initialize submission content with existing content
            setSubmissionContent(prev => ({
              ...prev,
              [assignment.id]: submissionRes.data.content
            }));
          }
        } catch (error) {
          // No submission exists yet, that's fine
        }
      }
      setAssignmentSubmissions(submissions);
    } catch (error) {
      console.error('Failed to load assignments:', error);
    }
  };

  const handleAssignmentSubmit = async (assignmentId, assignmentType) => {
    try {
      let content = submissionContent[assignmentId] || '';
      
      // Validation: Check if at least one field is filled
      const hasText = content && content.trim().length > 0;
      const hasImage = imageFile !== null;
      const hasVideo = assignmentType === 'video_link' && content && content.trim().length > 0;
      
      // Check if any submission content exists
      if (!hasText && !hasImage && !hasVideo) {
        toast.error('Please add text, an image, or a video link to submit the assignment.');
        return; // Stop submission
      }
      
      // For image upload, handle file upload
      if (assignmentType === 'image_upload' && imageFile) {
        // For now, we'll store the file name as content
        // In a real app, you'd upload to cloud storage and store the URL
        content = `image:${imageFile.name}`;
      }
      
      const submissionData = {
        assignment_id: assignmentId,
        submission_type: assignmentType,
        content: content
      };
      
      await axios.post(`${API}/assignments/${assignmentId}/submit`, submissionData, {
        headers: getAuthHeaders()
      });
      
      toast.success('Assignment submitted successfully!');
      loadAssignments(); // Reload to show submitted status
      onRefresh(); // Refresh to update lesson completion if needed
    } catch (error) {
      console.error('Failed to submit assignment:', error);
      toast.error('Failed to submit assignment');
    }
  };

  const openLesson = async () => {
    if (!canAccess) {
      toast.error('Complete the previous lesson quiz first');
      return;
    }
    setShowVideo(true);
    setVideoWatchTime(0);
    
    // Fetch quiz for this lesson
    try {
      const res = await axios.get(`${API}/quizzes/lesson/${lesson.id}`, { headers: getAuthHeaders() });
      if (res.data) {
        setQuiz(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch quiz:', error);
    }
    
    // Start video watch timer
    const timer = setInterval(() => {
      setVideoWatchTime(prev => prev + 1);
    }, 1000);
    
    // Auto-mark video as completed after 30 seconds (or when user closes)
    setTimeout(async () => {
      if (!videoMarkedComplete) {
        await markVideoComplete();
      }
    }, 30000);
    
    return () => clearInterval(timer);
  };

  const markVideoComplete = async () => {
    try {
      await axios.post(`${API}/progress/video-completed/${lesson.id}`, {}, {
        headers: getAuthHeaders()
      });
      setVideoMarkedComplete(true);
      console.log('Video marked as completed');
    } catch (error) {
      console.error('Failed to mark video complete:', error);
    }
  };

  const takeQuiz = () => {
    setShowVideo(false);
    setShowQuiz(true);
    setQuizAnswers({});
    setQuizResult(null);
  };

  const submitQuiz = async () => {
    try {
      // Validate quiz exists
      if (!quiz || !quiz.id) {
        console.error('Quiz not loaded:', quiz);
        toast.error('Quiz not loaded. Please try again.');
        return;
      }

      // Validate at least one answer is provided
      if (Object.keys(quizAnswers).length === 0) {
        toast.error('Please answer at least one question before submitting.');
        return;
      }

      console.log('Submitting quiz:', { quiz_id: quiz.id, answers: quizAnswers });

      const res = await axios.post(`${API}/quizzes/submit`, {
        quiz_id: quiz.id,
        answers: quizAnswers
      }, { headers: getAuthHeaders() });
      
      console.log('Quiz submission response:', res.data);
      console.log('Correct answer map:', res.data.correct_answer_map);
      console.log('User results:', res.data.user_results);

      // FIXED: Store result immediately so it's available for answer feedback
      setQuizResult(res.data);
      const submissionResult = res.data;
      
      // Show correct/incorrect answers for 1-2 seconds BEFORE closing dialog
      setShowAnswerFeedback(true);
      
      // Wait 1.5 seconds to show feedback while quiz dialog stays open
      await new Promise(resolve => setTimeout(resolve, 1500));
      setShowAnswerFeedback(false);
      
      // Add small transition delay before closing dialog
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Now close quiz dialog
      setShowQuiz(false);
      
      // Wait for dialog close transition
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Now process the result and show appropriate popup
      if (submissionResult.passed) {
        // Mark video as complete if not already done
        if (!videoMarkedComplete) {
          await markVideoComplete();
        }
        
        // CRITICAL: Immediately update local completion state
        setIsCompleted(true);
        setJustCompleted(true);
        
        // CRITICAL: Persist completion to localStorage to survive refreshes
        localStorage.setItem(`lesson_completed_${lesson.id}`, 'true');
        
        // CRITICAL: Add lesson to local completed set for instant progress update
        // and immediately recalculate optimistic progress
        setLocalCompletedLessons(prev => {
          const updated = new Set(prev);
          updated.add(lesson.id);
          
          // CRITICAL: Immediately recalculate optimistic progress with the updated set
          if (courseDetails?.modules) {
            const newOptimistic = calculateOptimisticProgress(
              courseDetails.modules,
              courseDetails.progress,
              updated  // Use the updated set, not the old state
            );
            setOptimisticProgress(newOptimistic);
            console.log('✓ Progress updated instantly:', newOptimistic.progress_percentage.toFixed(1) + '%');
          }
          
          return updated;
        });
        
        // CRITICAL: Force immediate lesson data refresh to get updated completion status (non-blocking)
        axios.get(`${API}/progress/lesson/${lesson.id}`, {
          headers: getAuthHeaders()
        }).then(progressRes => {
          if (progressRes.data.visually_completed === true) {
            console.log('✓ Lesson completion confirmed from server');
          }
        }).catch(error => {
          console.error('Failed to verify lesson completion:', error);
        });
        
        // Store result for celebration modal
        setQuizResult(submissionResult);
        
        // CRITICAL: Close video dialog to prevent flash during modal transition
        setShowVideo(false);
        
        // Small delay to ensure all state updates and dialog transitions are complete
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Show celebration modal (Congratulations popup)
        setShowQuizCelebration(true);
        setModuleCheckPending(true); // Mark that we should check module completion after celebration closes
        
        // REMOVED: Early refresh caused race condition during modal transitions
        // The refresh already happens in handleCelebrationClose() after user interaction
      } else {
        // Failed - show retry modal with encouragement
        setQuizResult(submissionResult);
        
        // Close video dialog to prevent flash
        setShowVideo(false);
        
        // Small delay for smooth transition
        await new Promise(resolve => setTimeout(resolve, 200));
        
        setShowQuizFail(true);
      }
    } catch (error) {
      console.error('Quiz submission error:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // Show more specific error message
      if (error.response?.data?.detail) {
        toast.error(`Failed to submit quiz: ${error.response.data.detail}`);
      } else if (error.response?.status === 404) {
        toast.error('Quiz not found. Please refresh and try again.');
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Authentication error. Please login again.');
      } else {
        toast.error('Failed to submit quiz. Please check your connection and try again.');
      }
    }
  };

  const handleQuizRetry = () => {
    setShowQuizFail(false);
    setQuizResult(null);
    setQuizAnswers({});
    // Reopen quiz modal
    setTimeout(() => {
      setShowQuiz(true);
    }, 300);
  };

  const checkModuleCompletion = async () => {
    // FIXED: Check if module is truly complete before showing popup
    const moduleId = lesson.module_id;
    const currentModule = courseDetails?.modules.find(m => m.id === moduleId);
    
    if (currentModule) {
      // Get all lessons in module
      const allLessonsInModule = currentModule.lessons || [];
      
      // Check if all lessons are completed (including the current one)
      const allCompleted = allLessonsInModule.every(l => {
        // Current lesson just completed, OR already marked as completed
        return l.id === lesson.id || l.completed || localCompletedLessons.has(l.id);
      });
      
      // FIXED: Only show module celebration if ALL lessons are complete AND there are lessons in the module
      if (allCompleted && allLessonsInModule.length > 0) {
        // Additional check: Verify at least one lesson exists and all are truly complete
        const hasAllRequirementsComplete = allLessonsInModule.every(l => {
          if (l.id === lesson.id) return true; // Just completed
          return l.completed === true; // Already completed
        });
        
        if (hasAllRequirementsComplete) {
          // Module is truly complete! Show celebration
          setCompletedModuleName(currentModule.title);
          setShowModuleCelebration(true);
        }
      }
    }
  };

  const checkMilestone = async () => {
    try {
      const res = await axios.get(`${API}/progress/milestones/${courseId}`, {
        headers: getAuthHeaders()
      });
      
      if (res.data.milestone) {
        setMilestoneData(res.data);
        
        // Show appropriate milestone modal
        switch(res.data.milestone) {
          case 25:
            setShowMilestone25(true);
            break;
          case 50:
            setShowMilestone50(true);
            break;
          case 75:
            setShowMilestone75(true);
            break;
          case 100:
            setShowMilestone100(true);
            break;
        }
      }
    } catch (error) {
      console.error('Failed to check milestone:', error);
    }
  };

  const handleCelebrationClose = async () => {
    setShowQuizCelebration(false);
    setShowVideo(false);
    
    let willShowModuleCelebration = false;
    
    // FIXED: Only check module completion if it was triggered by quiz
    if (moduleCheckPending) {
      setModuleCheckPending(false);
      
      // Check if module will be complete (before showing the modal)
      const moduleId = lesson.module_id;
      const currentModule = courseDetails?.modules.find(m => m.id === moduleId);
      
      if (currentModule) {
        const allLessonsInModule = currentModule.lessons || [];
        const allCompleted = allLessonsInModule.every(l => {
          return l.id === lesson.id || l.completed || localCompletedLessons.has(l.id);
        });
        
        if (allCompleted && allLessonsInModule.length > 0) {
          willShowModuleCelebration = true;
        }
      }
      
      await checkModuleCompletion();
    }
    
    // CRITICAL: Re-verify lesson completion status from server
    try {
      const progressRes = await axios.get(`${API}/progress/lesson/${lesson.id}`, {
        headers: getAuthHeaders()
      });
      
      if (progressRes.data.visually_completed === true) {
        setIsCompleted(true);
        setJustCompleted(true);
        localStorage.setItem(`lesson_completed_${lesson.id}`, 'true');
        console.log('✓ Lesson remains completed after celebration');
      }
    } catch (error) {
      console.error('Failed to re-verify completion:', error);
    }
    
    // Refresh course data
    onRefresh();
    
    // CRITICAL: Check for milestones ONLY if no module celebration will show
    // If module celebration will show, milestone check happens after that closes
    if (!willShowModuleCelebration) {
      await checkMilestone();
    }
  };

  const handleModuleCelebrationContinue = async () => {
    setShowModuleCelebration(false);
    onRefresh();
    
    // CRITICAL: Check for milestones after module celebration closes
    // This ensures milestone popups appear at the right time
    await checkMilestone();
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/)?.[1];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  return (
    <>
      <div
        className={`flex items-center justify-between p-4 rounded-2xl transition-smooth cursor-pointer ${
          isCompleted ? 'bg-green-50 border-2 border-green-200 hover:bg-green-100' : canAccess ? 'bg-[#F7F7F7] hover:bg-[#E7E7E7]' : 'bg-[#F7F7F7] opacity-60 cursor-not-allowed'
        }`}
        onClick={canAccess || isCompleted ? openLesson : null}
        data-testid={`lesson-item-${lesson.id}`}
        data-lesson-id={lesson.id}
      >
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isCompleted ? 'bg-green-500' : canAccess ? 'bg-primary-blue' : 'bg-gray-400'
          }`}>
            {isCompleted ? (
              <Check className="w-6 h-6 text-white" strokeWidth={3} />
            ) : canAccess ? (
              <PlayCircle className="w-5 h-5 text-white" strokeWidth={2} />
            ) : (
              <Lock className="w-5 h-5 text-white" strokeWidth={2} />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <p className={`font-semibold ${isCompleted ? 'text-green-700' : 'text-navy'}`}>
                Lesson {lessonIndex + 1}: {lesson.title}
              </p>
              {isCompleted && (
                <CheckCircle className="w-5 h-5 text-green-600" strokeWidth={2} />
              )}
            </div>
            {!canAccess && !isCompleted && <p className="text-xs text-[#A5A5A5] font-light">Complete previous lesson to unlock</p>}
            {isCompleted && <p className="text-xs text-green-600 font-medium">Completed ✓ Click to rewatch</p>}
          </div>
          {isCompleted && (
            <div className="text-sm font-medium text-green-600 bg-green-100 px-3 py-1 rounded-lg">
              Rewatch
            </div>
          )}
          {/* Submit Assignment Button */}
          {assignments.length > 0 && canAccess && (
            <SubmitAssignmentButton 
              assignments={assignments}
              assignmentSubmissions={assignmentSubmissions}
              onSubmissionUpdate={() => {
                loadAssignments();
                onRefresh();
              }}
              isLessonCompleted={isCompleted}
              triggerModal={triggerAssignmentModal && triggerAssignmentModal.lessonId === lesson.id}
              onModalTriggered={onAssignmentModalTriggered}
            />
          )}
        </div>
      </div>

      {/* Quiz Celebration Modal */}
      <QuizCelebrationModal
        isOpen={showQuizCelebration}
        onClose={handleCelebrationClose}
        score={quizResult?.score?.toFixed(0) || 0}
      />

      {/* Quiz Fail Modal */}
      <QuizFailModal
        isOpen={showQuizFail}
        onRetry={handleQuizRetry}
        onClose={() => setShowQuizFail(false)}
        score={quizResult?.score?.toFixed(0) || 0}
        passPercentage={quiz?.pass_percentage || 30}
      />

      {/* Module Celebration Modal */}
      <ModuleCelebrationModal
        isOpen={showModuleCelebration}
        onClose={() => setShowModuleCelebration(false)}
        moduleName={completedModuleName}
        onContinue={handleModuleCelebrationContinue}
      />

      {/* Milestone Celebration Modals */}
      <Milestone25Modal
        isOpen={showMilestone25}
        onClose={() => setShowMilestone25(false)}
        progress={milestoneData?.progress}
      />
      
      <Milestone50Modal
        isOpen={showMilestone50}
        onClose={() => setShowMilestone50(false)}
        progress={milestoneData?.progress}
      />
      
      <Milestone75Modal
        isOpen={showMilestone75}
        onClose={() => setShowMilestone75(false)}
        progress={milestoneData?.progress}
      />
      
      <Milestone100Modal
        isOpen={showMilestone100}
        onClose={() => setShowMilestone100(false)}
        progress={milestoneData?.progress}
        onViewLeaderboard={() => {
          setShowMilestone100(false);
          navigate('/leaderboard');
        }}
      />

      {/* Video Dialog */}
      <Dialog open={showVideo} onOpenChange={setShowVideo}>
        <DialogContent className="max-w-5xl rounded-3xl bg-white shadow-2xl border-0">
          <DialogHeader className="bg-primary-blue/10 -mx-6 -mt-6 px-6 py-4 mb-4 rounded-t-3xl">
            <DialogTitle className="text-2xl font-bold text-navy">Watch Video: {lesson.title}</DialogTitle>
          </DialogHeader>
          
          {/* Video Player */}
          <div className="aspect-video w-full rounded-2xl overflow-hidden border-2 border-[#E7E7E7]">
            <iframe
              width="100%"
              height="100%"
              src={getYouTubeEmbedUrl(lesson.video_url)}
              title={lesson.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>

          {/* Take Quiz Button - Always Visible */}
          {quiz ? (
            <div className="mt-6">
              <Button
                onClick={takeQuiz}
                className="w-full bg-primary-blue hover:bg-[#1874CD] text-white h-14 text-xl rounded-2xl transition-smooth btn-press font-bold shadow-lg"
                data-testid="take-quiz-button"
              >
                📝 Take Quiz
              </Button>
              {isCompleted && (
                <p className="text-center text-sm text-green-600 mt-2 font-medium">
                  ✓ You've already completed this quiz, but you can retake it anytime!
                </p>
              )}
            </div>
          ) : (
            <div className="mt-6 p-4 bg-[#F7F7F7] rounded-2xl text-center">
              <p className="text-[#4D4D4D] font-light">No quiz available for this lesson</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quiz Dialog */}
      <Dialog open={showQuiz} onOpenChange={setShowQuiz}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl bg-blue-50 shadow-2xl border-0">
          <DialogHeader className="bg-primary-blue/10 -mx-6 -mt-6 px-6 py-4 mb-4 rounded-t-3xl">
            <DialogTitle className="text-2xl font-bold text-navy">Quiz: {lesson.title}</DialogTitle>
            <p className="text-sm text-[#4D4D4D] font-medium mt-1">Pass with {quiz?.pass_percentage}% or higher to unlock the next lesson</p>
          </DialogHeader>
          {/* FIXED: Show questions during feedback OR when no result yet */}
          {(!quizResult || showAnswerFeedback) && (
            <div className="space-y-6">
              {quiz?.questions.map((question, index) => {
                // FIXED: Determine if answer is correct/incorrect for feedback using backend response
                const userAnswer = quizAnswers[index];
                const correctAnswer = showAnswerFeedback && quizResult?.correct_answer_map ? 
                  quizResult.correct_answer_map[index.toString()] : null;
                
                // Use backend's boolean result for this question
                const userAnsweredCorrectly = showAnswerFeedback && quizResult?.user_results?.[index.toString()] === true;
                
                return (
                  <div key={index} className="p-5 border-0 card-shadow rounded-3xl bg-white">
                    <p className="font-semibold text-navy mb-4">{index + 1}. {question.question}</p>
                    {question.type === 'mcq' ? (
                      <div className="space-y-2">
                        {question.options.map((option, optIndex) => {
                          const isSelected = quizAnswers[index] === option;
                          // Show green checkmark on the correct answer
                          const isCorrectOption = showAnswerFeedback && option === correctAnswer;
                          // Show red X on the user's selected answer if it was wrong
                          const isIncorrectOption = showAnswerFeedback && isSelected && !userAnsweredCorrectly;
                          
                          return (
                            <label 
                              key={optIndex} 
                              className={`flex items-center justify-between space-x-3 p-3 rounded-2xl cursor-pointer transition-smooth ${
                                isCorrectOption ? 'bg-green-100 border-2 border-green-500' :
                                isIncorrectOption ? 'bg-red-100 border-2 border-red-500' :
                                'bg-[#F7F7F7] hover:bg-[#E7E7E7]'
                              } ${showAnswerFeedback ? 'pointer-events-none' : ''}`}
                            >
                              <div className="flex items-center space-x-3">
                                <input
                                  type="radio"
                                  name={`question-${index}`}
                                  value={option}
                                  checked={quizAnswers[index] === option}
                                  onChange={(e) => setQuizAnswers({ ...quizAnswers, [index]: e.target.value })}
                                  className="w-4 h-4 text-primary-blue"
                                  data-testid={`quiz-option-${index}-${optIndex}`}
                                  disabled={showAnswerFeedback}
                                />
                                <span className={`text-sm ${
                                  isCorrectOption ? 'text-green-700 font-semibold' :
                                  isIncorrectOption ? 'text-red-700 font-semibold' :
                                  'text-navy'
                                }`}>{option}</span>
                              </div>
                              {isCorrectOption && (
                                <span className="text-green-600 font-bold text-xl">✓</span>
                              )}
                              {isIncorrectOption && (
                                <span className="text-red-600 font-bold text-xl">✗</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {['True', 'False'].map((value) => {
                          const isSelected = quizAnswers[index] === value;
                          // Show green checkmark on the correct answer
                          const isCorrectOption = showAnswerFeedback && value === correctAnswer;
                          // Show red X on the user's selected answer if it was wrong
                          const isIncorrectOption = showAnswerFeedback && isSelected && !userAnsweredCorrectly;
                          
                          return (
                            <label 
                              key={value}
                              className={`flex items-center justify-between space-x-3 p-3 rounded-2xl cursor-pointer transition-smooth ${
                                isCorrectOption ? 'bg-green-100 border-2 border-green-500' :
                                isIncorrectOption ? 'bg-red-100 border-2 border-red-500' :
                                'bg-[#F7F7F7] hover:bg-[#E7E7E7]'
                              } ${showAnswerFeedback ? 'pointer-events-none' : ''}`}
                            >
                              <div className="flex items-center space-x-3">
                                <input
                                  type="radio"
                                  name={`question-${index}`}
                                  value={value}
                                  checked={quizAnswers[index] === value}
                                  onChange={(e) => setQuizAnswers({ ...quizAnswers, [index]: e.target.value })}
                                  className="w-4 h-4 text-primary-blue"
                                  data-testid={`quiz-${value.toLowerCase()}-${index}`}
                                  disabled={showAnswerFeedback}
                                />
                                <span className={`text-sm ${
                                  isCorrectOption ? 'text-green-700 font-semibold' :
                                  isIncorrectOption ? 'text-red-700 font-semibold' :
                                  'text-navy'
                                }`}>{value}</span>
                              </div>
                              {isCorrectOption && (
                                <span className="text-green-600 font-bold text-xl">✓</span>
                              )}
                              {isIncorrectOption && (
                                <span className="text-red-600 font-bold text-xl">✗</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* FIXED: Hide button during feedback to prevent confusion */}
              {!showAnswerFeedback && (
                <Button
                  onClick={submitQuiz}
                  disabled={Object.keys(quizAnswers).length !== quiz?.questions.length}
                  className="w-full bg-primary-blue hover:bg-[#1874CD] text-white h-12 text-lg rounded-2xl transition-smooth btn-press disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-quiz-button"
                >
                  Submit Quiz
                </Button>
              )}
              {/* Show feedback message during answer review */}
              {showAnswerFeedback && (
                <div className="w-full p-4 bg-blue-100 border-2 border-blue-300 rounded-2xl text-center">
                  <p className="text-navy font-semibold">Reviewing your answers...</p>
                  <p className="text-sm text-[#4D4D4D] mt-1">Please wait while we check your responses</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function SubmitAssignmentButton({ assignments, assignmentSubmissions, onSubmissionUpdate, isLessonCompleted, triggerModal, onModalTriggered }) {
  const [showModal, setShowModal] = useState(false);
  const [submissionContent, setSubmissionContent] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(null);

  // Handle external trigger to open modal
  useEffect(() => {
    if (triggerModal) {
      setShowModal(true);
      if (onModalTriggered) {
        onModalTriggered();
      }
    }
  }, [triggerModal, onModalTriggered]);

  // Initialize submission content from existing submissions
  useEffect(() => {
    const initContent = {};
    assignments.forEach(assignment => {
      const submission = assignmentSubmissions[assignment.id];
      if (submission) {
        initContent[assignment.id] = submission.content;
      }
    });
    setSubmissionContent(initContent);
  }, [assignments, assignmentSubmissions]);

  const handleAssignmentSubmit = async (assignmentId, assignmentType) => {
    try {
      let content = submissionContent[assignmentId] || '';
      
      // Validation: Check if at least one field is filled
      const hasText = content && content.trim().length > 0;
      const hasImage = imageFile !== null;
      const hasVideo = assignmentType === 'video_link' && content && content.trim().length > 0;
      
      // Check if any submission content exists
      if (!hasText && !hasImage && !hasVideo) {
        toast.error('Please add text, an image, or a video link to submit the assignment.');
        return; // Stop submission
      }
      
      // For image upload, handle file upload
      if (assignmentType === 'image_upload' && imageFile) {
        // For now, we'll store the file name as content
        // In a real app, you'd upload to cloud storage and store the URL
        content = `image:${imageFile.name}`;
      }
      
      const submissionData = {
        assignment_id: assignmentId,
        submission_type: assignmentType,
        content: content
      };
      
      const response = await axios.post(`${API}/assignments/${assignmentId}/submit`, submissionData, {
        headers: getAuthHeaders()
      });
      
      // Close assignment modal and show success modal
      setShowModal(false);
      setSubmissionSuccess({
        assignmentId,
        content,
        timestamp: new Date().toLocaleString()
      });
      setShowSuccessModal(true);
      
      // Update data in background (don't call onRefresh immediately to prevent auto-opening video)
      setTimeout(() => {
        if (onSubmissionUpdate) onSubmissionUpdate();
      }, 100);
      
    } catch (error) {
      console.error('Failed to submit assignment:', error);
      toast.error('Failed to submit assignment');
    }
  };

  // Check if there are any submitted assignments
  const hasSubmissions = assignments.some(assignment => !!assignmentSubmissions[assignment.id]);

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation(); // Prevent lesson click handler
          setShowModal(true);
        }}
        className="assignment-submit-button bg-blue-50 hover:bg-blue-100 text-primary-blue border-2 border-blue-200 hover:border-primary-blue px-3 py-2 rounded-2xl text-sm font-semibold flex items-center space-x-2 transition-smooth"
      >
        <span>📝</span>
        <span>{hasSubmissions ? 'Update Assignment' : 'Submit Assignment'}</span>
      </button>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent 
          className="bg-[#E8F3FF] rounded-3xl shadow-2xl border-0 max-w-2xl max-h-[85vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-navy flex items-center space-x-2">
              <span>📝</span>
              <span>Assignment Submission</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 px-2">
            {assignments.map((assignment) => {
              const submission = assignmentSubmissions[assignment.id];
              const isSubmitted = !!submission;
              
              return (
                <div key={assignment.id} className="p-4 bg-white rounded-2xl border-2 border-blue-200">
                  {/* Assignment Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-navy flex items-center space-x-2">
                        <span>{assignment.title}</span>
                        {assignment.is_mandatory && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-semibold">
                            Mandatory
                          </span>
                        )}
                        {!assignment.is_mandatory && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-semibold">
                            Optional
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-[#4D4D4D] mt-1">{assignment.description}</p>
                    </div>
                    {isSubmitted && (
                      <div className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full font-semibold">
                        Submitted ✓
                      </div>
                    )}
                  </div>

                  {/* Show previous submission if exists */}
                  {isSubmitted && (
                    <div className="mb-4 p-3 bg-green-50 rounded-2xl border border-green-200">
                      <h4 className="text-sm font-semibold text-navy mb-2">Previous Submission:</h4>
                      <p className="text-sm text-[#4D4D4D]">{submission.content}</p>
                      <p className="text-xs text-green-600 mt-1">
                        Submitted on {new Date(submission.submitted_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}

                  {/* Submission Form */}
                  <div className="space-y-3">
                    {assignment.assignment_type === 'text' && (
                      <div>
                        <label className="block text-sm font-semibold text-navy mb-2">
                          {isSubmitted ? 'Update Your Answer:' : 'Your Answer:'}
                        </label>
                        <textarea
                          value={submissionContent[assignment.id] || ''}
                          onChange={(e) => {
                            e.stopPropagation();
                            setSubmissionContent({
                              ...submissionContent,
                              [assignment.id]: e.target.value
                            });
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          placeholder="Type your answer here..."
                          rows={4}
                          className="w-full p-3 border-2 border-[#E7E7E7] rounded-2xl focus:border-primary-blue transition-smooth bg-white"
                        />
                      </div>
                    )}

                    {assignment.assignment_type === 'video_link' && (
                      <div>
                        <label className="block text-sm font-semibold text-navy mb-2">
                          {isSubmitted ? 'Update Video URL:' : 'Video URL:'}
                        </label>
                        <input
                          type="url"
                          value={submissionContent[assignment.id] || ''}
                          onChange={(e) => {
                            e.stopPropagation();
                            setSubmissionContent({
                              ...submissionContent,
                              [assignment.id]: e.target.value
                            });
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          placeholder="https://youtube.com/watch?v=..."
                          className="w-full p-3 border-2 border-[#E7E7E7] rounded-2xl focus:border-primary-blue transition-smooth bg-white"
                        />
                      </div>
                    )}

                    {assignment.assignment_type === 'image_upload' && (
                      <div>
                        <label className="block text-sm font-semibold text-navy mb-2">
                          {isSubmitted ? 'Update Image:' : 'Upload Image:'}
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            e.stopPropagation();
                            setImageFile(e.target.files[0]);
                            // Update content with file name for display
                            if (e.target.files[0]) {
                              setSubmissionContent({
                                ...submissionContent,
                                [assignment.id]: e.target.files[0].name
                              });
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          className="w-full p-3 border-2 border-[#E7E7E7] rounded-2xl focus:border-primary-blue transition-smooth bg-white"
                        />
                        {imageFile && (
                          <p className="text-sm text-green-600 mt-2">Selected: {imageFile.name}</p>
                        )}
                      </div>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleAssignmentSubmit(assignment.id, assignment.assignment_type);
                      }}
                      disabled={!submissionContent[assignment.id] && assignment.assignment_type !== 'image_upload'}
                      className="w-full bg-primary-blue hover:bg-[#1874CD] disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-2xl transition-smooth"
                    >
                      {isSubmitted ? 'Update Submission' : 'Submit Assignment'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="sticky bottom-0 bg-[#E8F3FF] pt-4 pb-2 -mx-2 px-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowModal(false);
              }}
              className="w-full bg-white hover:bg-gray-50 text-navy border-2 border-[#E7E7E7] font-semibold py-3 px-4 rounded-2xl transition-smooth"
            >
              Close
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assignment Submission Success Modal */}
      {showSuccessModal && submissionSuccess && (
        <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
          <DialogContent 
            className="bg-[#E8F3FF] rounded-3xl shadow-2xl border-0 max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-navy flex items-center space-x-2">
                <span>✅</span>
                <span>Assignment Submitted</span>
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 px-2">
              <div className="p-4 bg-green-50 rounded-2xl border-2 border-green-200">
                <h4 className="font-bold text-navy mb-2">Submission Successful!</h4>
                <p className="text-sm text-[#4D4D4D] mb-2">
                  <strong>Submitted:</strong> {submissionSuccess.timestamp}
                </p>
                <p className="text-sm text-[#4D4D4D]">
                  <strong>Content:</strong> {submissionSuccess.content.substring(0, 100)}
                  {submissionSuccess.content.length > 100 ? '...' : ''}
                </p>
              </div>
              
              <p className="text-sm text-[#4D4D4D] text-center">
                Your assignment has been submitted successfully. Lesson completion status will be updated automatically.
              </p>
            </div>
            
            <div className="space-y-3 px-2">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSuccessModal(false);
                  setShowModal(true); // Reopen assignment modal for resubmission
                }}
                variant="outline"
                className="w-full border-2 border-primary-blue text-primary-blue hover:bg-blue-50 rounded-2xl"
              >
                Resubmit Assignment
              </Button>
              
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSuccessModal(false);
                }}
                className="w-full bg-primary-blue hover:bg-[#1874CD] text-white rounded-2xl"
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// Assignment Prompt Modal Component
// AssignmentPromptModal moved to /app/frontend/src/components/course/AssignmentPromptModal.jsx